'use client'

import { OtpSessionResponse } from '@/hooks/useOtpLogin'
import Button from 'lib/components/Button/Button'
import { TextInputStandalone } from 'lib/components/Form'
import Header from 'lib/components/Header/Header'
import Link from 'lib/components/Link/Link'
import Notification from 'lib/components/Notification/Notification'
import { useEffect, useRef, useState } from 'react'

interface OtpVerifyProps {
  email: string
  onBack: () => void
  onSuccess: (response: OtpSessionResponse) => void
  onResend: () => void
  verifying: boolean
  requesting: boolean
  verifyError: string | null
  requestError: string | null
  verifyOtp: (
    email: string,
    code: string,
    cartToken?: string,
  ) => Promise<OtpSessionResponse | null>
  cartToken?: string
}

export default function OtpVerify({
  email,
  onBack,
  onSuccess,
  onResend,
  verifying,
  requesting,
  verifyError,
  requestError,
  verifyOtp,
  cartToken,
}: OtpVerifyProps) {
  const [code, setCode] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return (): void => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  const handleResend = (): void => {
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    onResend()
    setResendCooldown(30)
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const res = await verifyOtp(email, code, cartToken)
    if (res) onSuccess(res)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Header as="h2" className="type-body-3 mb-4">
        Enter verification code
      </Header>
      <p className="type-body-8 mb-4">
        If a matching account is found, a code will be sent via SMS to the
        mobile number on the account.
      </p>
      {(verifyError || requestError) && (
        <Notification
          size="small"
          icon={{ icon: 'error' }}
          type="urgent"
          title={String(requestError || verifyError || '')}
          className="mb-4 !text-left"
        />
      )}
      <TextInputStandalone
        label="Verification code*"
        placeholder="Enter 6-digit code"
        name="otpCode"
        inputMode="numeric"
        maxLength={6}
        disabled={verifying}
        onChange={(val) => setCode(String(val ?? ''))}
      />
      <Button
        type="submit"
        className="!mt-4 w-full !type-body-11-bold md:!type-body-5"
        disabled={verifying || code.length < 6}
      >
        {verifying ? 'Verifying...' : 'Verify Code'}
      </Button>
      <div className="mt-4 flex items-center justify-between">
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault()
            onBack()
          }}
          className="md:!text-xs"
        >
          Back
        </Link>
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleResend()
          }}
          className="md:!text-xs"
          disabled={requesting || resendCooldown > 0}
        >
          {requesting
            ? 'Sending...'
            : resendCooldown > 0
              ? `Resend code (${resendCooldown}s)`
              : 'Resend code'}
        </Link>
      </div>
    </form>
  )
}
